import { atom } from 'jotai';

import { purchaseOrdersForLinkQueryAtom } from '../_graphql/queryPurchaseOrdersForLink';
import { salesOrdersForLinkQueryAtom } from '../_graphql/querySalesOrdersForLink';
import { OrderRecordForLink } from '../types/brokeredOrder';

import { $linkDialogType } from './brokeredOrderDetail.state';

// 🔘🔘🔘 PLAIN ATOMS
export const $linkDialogSearchTerm = atom('');
export const $linkDialogAccountId = atom<string | null>(null);
export const $linkDialogSelectedIds = atom<Set<string>>(new Set<string>());
export const $linkDialogExistingOrderIds = atom<string[]>([]);

// 🟢🟢🟢 REMOTE DATA -> purchase orders for link
export const $remotePurchaseOrdersForLink = purchaseOrdersForLinkQueryAtom(
  (get) => {
    const accountId = get($linkDialogAccountId);
    return {
      variables: {
        searchTerm: get($linkDialogSearchTerm) || undefined,
        accountIds: accountId ? [accountId] : undefined,
      },
      skip: get($linkDialogType) !== 'purchaseOrder',
    };
  },
);

// 🟢🟢🟢 REMOTE DATA -> sales orders for link
export const $remoteSalesOrdersForLink = salesOrdersForLinkQueryAtom((get) => {
  const accountId = get($linkDialogAccountId);
  return {
    variables: {
      searchTerm: get($linkDialogSearchTerm) || undefined,
      accountIds: accountId ? [accountId] : undefined,
    },
    skip: get($linkDialogType) !== 'salesOrder',
  };
});

// 🟢🟢🟢 COMPUTED DATA -> available orders (filtered by existing IDs)
export const $linkDialogOrders = atom<{
  orders: OrderRecordForLink[];
  isLoading: boolean;
  total: number;
}>((get) => {
  const orderType = get($linkDialogType);
  const existingOrderIds = get($linkDialogExistingOrderIds);

  const queryResult =
    orderType === 'purchaseOrder'
      ? get($remotePurchaseOrdersForLink)
      : get($remoteSalesOrdersForLink);

  const allOrders = queryResult.data?.orders ?? [];
  const filteredOrders = allOrders.filter(
    (order) => !existingOrderIds.includes(order.id),
  );

  return {
    orders: filteredOrders,
    isLoading: queryResult.isLoading ?? false,
    total: filteredOrders.length,
  };
});

// 🟠🟠🟠 ACTION -> reset dialog state
export const $resetLinkDialog = atom(null, (_get, set) => {
  set($linkDialogSearchTerm, '');
  set($linkDialogAccountId, null);
  set($linkDialogSelectedIds, new Set());
});

// 🟠🟠🟠 ACTION -> toggle selection
export const $toggleLinkDialogSelection = atom(
  null,
  (get, set, orderId: string) => {
    const current = get($linkDialogSelectedIds);
    const next = new Set(current);
    if (next.has(orderId)) {
      next.delete(orderId);
    } else {
      next.add(orderId);
    }
    set($linkDialogSelectedIds, next);
  },
);

// 🟠🟠🟠 ACTION -> clear selection
export const $clearLinkDialogSelection = atom(null, (_get, set) => {
  set($linkDialogSelectedIds, new Set());
});
